import { useState } from 'react'
import { InfiniteData, useQueryClient } from '@tanstack/react-query'

import { BankService } from '@/api/services/BankService'
import { ITransactionsResponse } from '@/api/services/CustomerService'
import { ITransaction } from '@/types'
import { DescriptionAlias } from './aliasOptions'
import { AliasRunResult, planAliasBatches, runAliasBatches } from './bulkAlias'

type TransactionPages = InfiniteData<ITransactionsResponse>

export const useBulkAliasUpdate = (userId: string | undefined) => {
  const queryClient = useQueryClient()
  const [isApplying, setIsApplying] = useState(false)
  const [done, setDone] = useState(0)
  const [total, setTotal] = useState(0)

  // Written straight into the rows already read rather than refetched: a search may have walked
  // every page, and a refetch would walk them all again just to pick up the new alias.
  const markUpdated = (ownerId: string, ids: string[], alias: DescriptionAlias) => {
    const updated = new Set(ids)
    queryClient.setQueryData<TransactionPages>(['transactions', ownerId], cached =>
      cached
        ? {
            ...cached,
            pages: cached.pages.map(page => ({
              ...page,
              data: page.data.map(row =>
                updated.has(row.transactionId) ? { ...row, descriptionAlias: alias } : row
              ),
            })),
          }
        : cached
    )
  }

  // Drops what the panel holds for the customer's transactions after a failed batch. The backend
  // may have applied part of it without saying how far it got, so the rows are read again rather
  // than guessed at.
  const reconcile = () => queryClient.invalidateQueries({ queryKey: ['transactions', userId] })

  const apply = async (rows: ITransaction[], alias: DescriptionAlias): Promise<AliasRunResult> => {
    // Fixed for the whole run, so every batch and every cache write stays with the customer whose
    // rows were reviewed even if another one is selected meanwhile.
    const ownerId = userId
    if (!ownerId) {
      throw new Error('No customer is selected.')
    }
    const batches = planAliasBatches(rows)
    setIsApplying(true)
    setDone(0)
    setTotal(batches.reduce((sum, batch) => sum + batch.transactionIds.length, 0))

    try {
      return await runAliasBatches(
        batches,
        batch =>
          BankService.bulkUpdateDescriptionAlias(
            ownerId,
            batch.accountId,
            batch.transactionIds.map(transactionId => ({ transactionId, descriptionAlias: alias }))
          ),
        ids => {
          markUpdated(ownerId, ids, alias)
          setDone(current => current + ids.length)
        }
      )
    } finally {
      setIsApplying(false)
    }
  }

  return { apply, reconcile, isApplying, progress: { done, total } }
}
